<template>

  <!-- A completed task, read-only, in the task panel design (US 9478) -->
  <TaskPanelFrame ref="view-task-modal"
                  qid="view-task-panel"
                  :title="selectedTaskObject.full_title || selectedTaskObject.title || ''"
                  :crumb="selectedTaskObject.project ? [selectedTaskObject.project.title] : []"
                  :status="selectedTaskObject.status || ''"
                  :users="selectedTaskObject.users || []"
                  @close="hideViewTaskModal"
  >
    <template #meta>
      <span data-qid="view-task-modified-date">
        {{ $t('table.last_updated') }} <b>{{ selectedTaskObject.updated_at | formatDate }}</b>
        <span v-if="selectedTaskObject.updated_by" data-qid="view-task-modified-by">· {{ selectedTaskObject.updated_by.name }}</span>
      </span>
    </template>

    <div class="task-control" v-if="viewDescription" data-qid="view-task-description">
      <div class="task-control__text text-formatted">{{ showFullDescription ? viewDescription : $options.filters.truncate(viewDescription, 300) }}</div>
      <span v-if="viewDescription.length > 300" class="v2-link cursor-pointer" @click="showFullDescription = !showFullDescription">
        {{ showFullDescription ? $t('system.view_less') : $t('system.view_more') }}
      </span>
    </div>

    <section class="v2-field task-panel-form" data-qid="view-task-form">
    <b-form qid="view-task-form" v-if="selectedTaskObject && selectedTaskObject.project">
      <div class="form-container" v-if="selectedTaskObject && selectedTaskObject.code !== 'system_impact_assessment' && selectedTaskObject.code !== 'risk_assessment' && selectedTaskObject.code !== 'control_assessment'">
        <b-form-group class="mb-4">
          <div v-if="selectedTaskObject.scope">
            <font-awesome-icon icon="shield-alt" size="lg" class="mr-2"/>
            {{ selectedTaskObject.scope.name }}
          </div>
          <div v-if="selectedTaskObject.model && selectedTaskObject.project && selectedTaskObject.project.type !== 'security_measure'" class="mt-3">
            <span v-if="selectedTaskObject.model">
              <font-awesome-icon
                icon="desktop"
                size="lg"
                class="mr-2"
                v-if="selectedTaskObject.model.who_am_i === 'App\\System\\Models\\System'"
              />
              <font-awesome-icon
                icon="cubes"
                size="lg"
                class="mr-2"
                v-if="selectedTaskObject.model.who_am_i === 'App\\Activity\\Models\\Activity'"
              />
              <font-awesome-icon
                icon="cube"
                size="lg"
                class="mr-2"
                v-if="selectedTaskObject.model.who_am_i === 'App\\Process\\Models\\Process'"
              />
              <font-awesome-icon
                icon="file-signature"
                size="lg"
                class="mr-2"
                v-if="selectedTaskObject.model.who_am_i === 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty'"
              />
            </span>
            {{ selectedTaskObject.model.name }}
          </div>
        </b-form-group>

        <b-form-group>
          <label data-qid="view-task-project_message-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.project_message_label') }}
          </label>
          <div v-if="selectedTaskObject.project.schedule_message && selectedTaskObject.project.schedule_message.length">
            <p v-if="showFullProjectMessage" class="text-formatted" style="margin-top: -20px;">
              {{ selectedTaskObject.project.schedule_message }}
            </p>
            <p v-if="!showFullProjectMessage" class="text-formatted" style="margin-top: -20px;">
              {{ selectedTaskObject.project.schedule_message | truncate(300) }}
            </p>
            <div v-if="selectedTaskObject.project.schedule_message.length > 300" class="text-center mt-3" @click="showFullProjectMessage=!showFullProjectMessage">
              <span class="cursor-pointer" v-if="!showFullProjectMessage">
                <span class="mr-2">
                  {{ $t('system.view_more') }}
                </span>
                <font-awesome-icon icon="chevron-down"/>
              </span>
              <span class="cursor-pointer" v-if="showFullProjectMessage">
                <span class="mr-2">
                  {{ $t('system.view_less') }}
                </span>
                <font-awesome-icon icon="chevron-up"/>
              </span>
            </div>
          </div>
          <p v-if="selectedTaskObject.project.schedule_message && selectedTaskObject.project.schedule_message.length <= 300"
             class="text-formatted"
             style="margin-top: -20px;"
          >
            {{ selectedTaskObject.project.schedule_message }}
          </p>
        </b-form-group>

        <b-form-group v-if="selectedTaskObject && selectedTaskObject.scope">
          <label data-qid="view-task-generic_control_description-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.generic_control_description_label') }}
          </label>
          <div v-if="selectedTaskObject.scope.description && selectedTaskObject.scope.description.length">
            <p v-if="showFullGenericControlDescription" class="text-formatted" style="margin-top: -20px;">
              {{ selectedTaskObject.scope.description }}
            </p>
            <p v-if="!showFullGenericControlDescription" class="text-formatted" style="margin-top: -20px;">
              {{ selectedTaskObject.scope.description | truncate(300) }}
            </p>
            <div v-if="selectedTaskObject.scope.description.length > 300" class="text-center mt-3" @click="showFullGenericControlDescription=!showFullGenericControlDescription">
              <span class="cursor-pointer" v-if="!showFullGenericControlDescription">
                <span class="mr-2">
                  {{ $t('system.view_more') }}
                </span>
                <font-awesome-icon icon="chevron-down"/>
              </span>
              <span class="cursor-pointer" v-if="showFullGenericControlDescription">
                <span class="mr-2">
                  {{ $t('system.view_less') }}
                </span>
                <font-awesome-icon icon="chevron-up"/>
              </span>
            </div>
          </div>
          <p v-if="selectedTaskObject.scope.description && selectedTaskObject.scope.description.length <= 300"
             class="text-formatted"
             style="margin-top: -20px;"
          >
            {{ selectedTaskObject.scope.description }}
          </p>
        </b-form-group>

        <b-form-group>
          <label data-qid="view-task-status-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.status_label') }}
          </label>
          <p>
            <ControlResultBadge :value="selectedTaskObject.completion_status"></ControlResultBadge>
          </p>
        </b-form-group>

        <b-form-group>
          <label data-qid="view-task-remarks-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.remarks_label') }}
          </label>
          <p class="text-formatted" style="margin-top: -20px;"><FormattedNoteText :value="selectedTaskObject.remarks"/></p>
        </b-form-group>

        <div>
          <label class="mt-3" style="font-size: medium;">
            {{ $t('maintenance.policies.create.attachments') }}
          </label>
          <br>
          <p v-for="file in selectedTaskObject.media" :key="file.id">
            <span class="mr-3 float-right text-muted cursor-pointer"
                  @click="downloadMedia(file)"
                  data-qid="view-task-download-media"
            >
              <font-awesome-icon icon="cloud-download-alt" />
            </span>
            <a href="#" @click="downloadMedia(file)" data-qid="view-task-media-item">
              {{ file.file_name }}
            </a>
          </p>
        </div>

        <div>
          <label class="mt-3" style="font-size: medium;">
            {{ $t('maintenance.policies.create.links_label') }}
          </label>
          <div v-if="selectedTaskObject.links">
            <p v-for="link in selectedTaskObject.links" :key="link.id">
              <a :href="link.url" target="_blank" data-qid="view-task-link-item">
                {{ link.name ? link.name : link.url }}
              </a>
            </p>
          </div>
          <br>
        </div>
      </div>

      <div class="form-container" v-if="selectedTaskObject && selectedTaskObject.code === 'risk_assessment'">
        <b-form-group class="mb-4">
          <div v-if="selectedTaskObject.scope">
            <font-awesome-icon icon="shield-alt" size="lg" class="mr-2"/>
            {{ selectedTaskObject.scope.name }}
          </div>
          <div v-if="selectedTaskObject.model && selectedTaskObject.project && selectedTaskObject.project.type !== 'security_measure'" class="mt-3">
            <span v-if="selectedTaskObject.model">
              <font-awesome-icon
                icon="desktop"
                size="lg"
                class="mr-2"
                v-if="selectedTaskObject.model.who_am_i === 'App\\System\\Models\\System'"
              />
              <font-awesome-icon
                icon="cubes"
                size="lg"
                class="mr-2"
                v-if="selectedTaskObject.model.who_am_i === 'App\\Activity\\Models\\Activity'"
              />
              <font-awesome-icon
                icon="cube"
                size="lg"
                class="mr-2"
                v-if="selectedTaskObject.model.who_am_i === 'App\\Process\\Models\\Process'"
              />
              <font-awesome-icon
                icon="file-signature"
                size="lg"
                class="mr-2"
                v-if="selectedTaskObject.model.who_am_i === 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty'"
              />
            </span>
            {{ selectedTaskObject.model.name }}
          </div>
        </b-form-group>

        <b-form-group>
          <label data-qid="view-task-project_message-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.project_message_label') }}
          </label>
          <div v-if="selectedTaskObject.project.schedule_message && selectedTaskObject.project.schedule_message.length">
            <p v-if="showFullProjectMessage" class="text-formatted" style="margin-top: -20px;">
              {{ selectedTaskObject.project.schedule_message }}
            </p>
            <p v-if="!showFullProjectMessage" class="text-formatted" style="margin-top: -20px;">
              {{ selectedTaskObject.project.schedule_message | truncate(300) }}
            </p>
            <div v-if="selectedTaskObject.project.schedule_message.length > 300" class="text-center mt-3" @click="showFullProjectMessage=!showFullProjectMessage">
              <span class="cursor-pointer" v-if="!showFullProjectMessage">
                <span class="mr-2">
                  {{ $t('system.view_more') }}
                </span>
                <font-awesome-icon icon="chevron-down"/>
              </span>
              <span class="cursor-pointer" v-if="showFullProjectMessage">
                <span class="mr-2">
                  {{ $t('system.view_less') }}
                </span>
                <font-awesome-icon icon="chevron-up"/>
              </span>
            </div>
          </div>
          <p v-if="selectedTaskObject.project.schedule_message && selectedTaskObject.project.schedule_message.length <= 300"
             class="text-formatted"
             style="margin-top: -20px;"
          >
            {{ selectedTaskObject.project.schedule_message }}
          </p>
        </b-form-group>

        <b-form-group>
          <label data-qid="view-task-response-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.response_label') }}
          </label>
          <p>
            {{ selectedTaskObject.response }}
          </p>
        </b-form-group>

        <b-form-group>
          <label data-qid="view-task-remarks-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.remarks_label') }}
          </label>
          <p class="text-formatted" style="margin-top: -20px;"><FormattedNoteText :value="selectedTaskObject.remarks"/></p>
        </b-form-group>

        <div>
          <label class="mt-3" style="font-size: medium;">
            {{ $t('maintenance.policies.create.attachments') }}
          </label>
          <br>
          <p v-for="file in selectedTaskObject.media" :key="file.id">
            <span class="mr-3 float-right text-muted cursor-pointer"
                  @click="downloadMedia(file)"
                  data-qid="view-task-download-media"
            >
              <font-awesome-icon icon="cloud-download-alt" />
            </span>
            <a href="#" @click="downloadMedia(file)" data-qid="view-task-media-item">
              {{ file.file_name }}
            </a>
          </p>
        </div>

        <div>
          <label class="mt-3" style="font-size: medium;">
            {{ $t('maintenance.policies.create.links_label') }}
          </label>
          <div v-if="selectedTaskObject.links">
            <p v-for="link in selectedTaskObject.links" :key="link.id">
              <a :href="link.url" target="_blank" data-qid="view-task-link-item">
                {{ link.name ? link.name : link.url }}
              </a>
            </p>
          </div>
          <br>
        </div>
      </div>

      <div class="form-container" v-if="selectedTaskObject && selectedTaskObject.code === 'system_impact_assessment'">
        <b-form-group class="mb-4">
          <div v-if="selectedTaskObject.scope">
            <font-awesome-icon icon="shield-alt" size="lg" class="mr-2"/>
            {{ selectedTaskObject.scope.name }}
          </div>
          <div v-if="selectedTaskObject.model && selectedTaskObject.project && selectedTaskObject.project.type !== 'security_measure'" class="mt-3">
            <span v-if="selectedTaskObject.model">
              <font-awesome-icon
                icon="desktop"
                size="lg"
                class="mr-2"
                v-if="selectedTaskObject.model.who_am_i === 'App\\System\\Models\\System'"
              />
              <font-awesome-icon
                icon="cubes"
                size="lg"
                class="mr-2"
                v-if="selectedTaskObject.model.who_am_i === 'App\\Activity\\Models\\Activity'"
              />
              <font-awesome-icon
                icon="cube"
                size="lg"
                class="mr-2"
                v-if="selectedTaskObject.model.who_am_i === 'App\\Process\\Models\\Process'"
              />
              <font-awesome-icon
                icon="file-signature"
                size="lg"
                class="mr-2"
                v-if="selectedTaskObject.model.who_am_i === 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty'"
              />
            </span>
            {{ selectedTaskObject.model.name }}
          </div>
        </b-form-group>

        <b-form-group>
          <label data-qid="view-task-project_message-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.project_message_label') }}
          </label>
          <div v-if="selectedTaskObject.project.schedule_message && selectedTaskObject.project.schedule_message.length">
            <p v-if="showFullProjectMessage" class="text-formatted" style="margin-top: -20px;">
              {{ selectedTaskObject.project.schedule_message }}
            </p>
            <p v-if="!showFullProjectMessage" class="text-formatted" style="margin-top: -20px;">
              {{ selectedTaskObject.project.schedule_message | truncate(300) }}
            </p>
            <div v-if="selectedTaskObject.project.schedule_message.length > 300" class="text-center mt-3" @click="showFullProjectMessage=!showFullProjectMessage">
              <span class="cursor-pointer" v-if="!showFullProjectMessage">
                <span class="mr-2">
                  {{ $t('system.view_more') }}
                </span>
                <font-awesome-icon icon="chevron-down"/>
              </span>
              <span class="cursor-pointer" v-if="showFullProjectMessage">
                <span class="mr-2">
                  {{ $t('system.view_less') }}
                </span>
                <font-awesome-icon icon="chevron-up"/>
              </span>
            </div>
          </div>
          <p v-if="selectedTaskObject.project.schedule_message && selectedTaskObject.project.schedule_message.length <= 300"
             class="text-formatted"
             style="margin-top: -20px;"
          >
            {{ selectedTaskObject.project.schedule_message }}
          </p>
        </b-form-group>

        <b-form-group>
          <label data-qid="view-task-confidentiality-label" class="mt-3" style="font-size: medium;">
            {{ $t('processes.confidentiality.title') }}
          </label>
          <p>
            {{ selectedTaskObject.confidentiality }}
          </p>
        </b-form-group>

        <b-form-group>
          <label data-qid="view-task-integrity-label" class="mt-3" style="font-size: medium;">
            {{ $t('processes.integrity.title') }}
          </label>
          <p>
            {{ selectedTaskObject.integrity }}
          </p>
        </b-form-group>

        <b-form-group>
          <label data-qid="view-task-availability-label" class="mt-3" style="font-size: medium;">
            {{ $t('processes.availability.title') }}
          </label>
          <p>
            {{ selectedTaskObject.availability }}
          </p>
        </b-form-group>

        <b-form-group>
          <label data-qid="view-task-rto_number-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.rto_label') }}
          </label>
          <p>
            {{ selectedTaskObject.rto_number }} {{ selectedTaskObject.rto_type }}
          </p>
        </b-form-group>

        <b-form-group>
          <label data-qid="view-task-rpo_type-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.rpo_label') }}
          </label>
          <p>
            {{ selectedTaskObject.rpo_number }} {{ selectedTaskObject.rpo_type }}
          </p>
        </b-form-group>

        <b-form-group>
          <label data-qid="view-task-remarks-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.remarks_label') }}
          </label>
          <p class="text-formatted" style="margin-top: -20px;"><FormattedNoteText :value="selectedTaskObject.remarks"/></p>
        </b-form-group>
      </div>

      <div class="form-container" v-if="selectedTaskObject && selectedTaskObject.code === 'control_assessment'">
        <b-form-group v-if="selectedTaskObject.project.schedule_message">
          <label data-qid="view-task-project_message-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.project_message_label') }}
          </label>
          <div v-if="selectedTaskObject.project.schedule_message && selectedTaskObject.project.schedule_message.length">
            <p v-if="showFullProjectMessage" class="text-formatted" style="margin-top: -20px;">
              {{ selectedTaskObject.project.schedule_message }}
            </p>
            <p v-if="!showFullProjectMessage" class="text-formatted" style="margin-top: -20px;">
              {{ selectedTaskObject.project.schedule_message | truncate(300) }}
            </p>
            <div v-if="selectedTaskObject.project.schedule_message.length > 300" class="text-center mt-3" @click="showFullProjectMessage=!showFullProjectMessage">
              <span class="cursor-pointer" v-if="!showFullProjectMessage">
                <span class="mr-2">
                  {{ $t('system.view_more') }}
                </span>
                <font-awesome-icon icon="chevron-down"/>
              </span>
              <span class="cursor-pointer" v-if="showFullProjectMessage">
                <span class="mr-2">
                  {{ $t('system.view_less') }}
                </span>
                <font-awesome-icon icon="chevron-up"/>
              </span>
            </div>
          </div>
          <p v-if="selectedTaskObject.project.schedule_message && selectedTaskObject.project.schedule_message.length <= 300"
             class="text-formatted"
             style="margin-top: -20px;"
          >
            {{ selectedTaskObject.project.schedule_message }}
          </p>
        </b-form-group>

        <b-form-group v-if="!ownerTask && ownerDataCollection && selectedTaskObject.data_remarks">
          <label data-qid="view-task-dc-control_description-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.control_description_label') }}
          </label>

          <div v-if="selectedTaskObject.data_remarks && selectedTaskObject.data_remarks.length">
            <p v-if="showFullControlDescription" class="text-formatted" style="margin-top: -20px;">
              <FormattedNoteText :value="selectedTaskObject.data_remarks"/>
            </p>
            <p v-if="!showFullControlDescription" class="text-formatted" style="margin-top: -20px;">
              <FormattedNoteText :value="selectedTaskObject.data_remarks" :truncate="300"/>
            </p>
            <div v-if="selectedTaskObject.data_remarks.length > 300" class="text-center mt-3" @click="showFullControlDescription=!showFullControlDescription">
              <span class="cursor-pointer" v-if="!showFullControlDescription">
                <span class="mr-2">
                  {{ $t('system.view_more') }}
                </span>
                <font-awesome-icon icon="chevron-down"/>
              </span>
              <span class="cursor-pointer" v-if="showFullControlDescription">
                <span class="mr-2">
                  {{ $t('system.view_less') }}
                </span>
                <font-awesome-icon icon="chevron-up"/>
              </span>
            </div>
          </div>
        </b-form-group>

        <b-form-group v-if="ownerTask && selectedTaskObject.control_description">
          <label data-qid="view-task-control_description-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.control_description_label') }}
          </label>

          <div v-if="selectedTaskObject.control_description && selectedTaskObject.control_description.length">
            <p v-if="showFullControlDescription" class="text-formatted" style="margin-top: -20px;">
              <FormattedNoteText :value="selectedTaskObject.control_description"/>
            </p>
            <p v-if="!showFullControlDescription" class="text-formatted" style="margin-top: -20px;">
              <FormattedNoteText :value="selectedTaskObject.control_description" :truncate="300"/>
            </p>
            <div v-if="selectedTaskObject.control_description.length > 300" class="text-center mt-3" @click="showFullControlDescription=!showFullControlDescription">
              <span class="cursor-pointer" v-if="!showFullControlDescription">
                <span class="mr-2">
                  {{ $t('system.view_more') }}
                </span>
                <font-awesome-icon icon="chevron-down"/>
              </span>
              <span class="cursor-pointer" v-if="showFullControlDescription">
                <span class="mr-2">
                  {{ $t('system.view_less') }}
                </span>
                <font-awesome-icon icon="chevron-up"/>
              </span>
            </div>
          </div>
        </b-form-group>

        <b-form-group v-if="ownerTask && selectedTaskObject.performed_text">
          <label data-qid="view-task-performed_test-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.performed_test_label') }}
          </label>

          <div v-if="selectedTaskObject.performed_text && selectedTaskObject.performed_text.length">
            <p v-if="showFullPerformedTest" class="text-formatted" style="margin-top: -20px;">
              <FormattedNoteText :value="selectedTaskObject.performed_text"/>
            </p>
            <p v-if="!showFullPerformedTest" class="text-formatted" style="margin-top: -20px;">
              <FormattedNoteText :value="selectedTaskObject.performed_text" :truncate="300"/>
            </p>
            <div v-if="selectedTaskObject.performed_text.length > 300" class="text-center mt-3" @click="showFullPerformedTest=!showFullPerformedTest">
              <span class="cursor-pointer" v-if="!showFullPerformedTest">
                <span class="mr-2">
                  {{ $t('system.view_more') }}
                </span>
                <font-awesome-icon icon="chevron-down"/>
              </span>
              <span class="cursor-pointer" v-if="showFullPerformedTest">
                <span class="mr-2">
                  {{ $t('system.view_less') }}
                </span>
                <font-awesome-icon icon="chevron-up"/>
              </span>
            </div>
          </div>
        </b-form-group>

        <b-form-group v-if="ownerTask && selectedTaskObject.response">
          <label data-qid="view-task-response-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.response_label') }}
          </label>
          <p>
            {{ selectedTaskObject.response }}
          </p>
        </b-form-group>

        <b-form-group v-if="ownerTask && selectedTaskObject.remarks">
          <label data-qid="view-task-remarks-label" class="mt-3" style="font-size: medium;">
            {{ $t('tasks.modal.remarks_label') }}
          </label>

          <div v-if="selectedTaskObject.remarks && selectedTaskObject.remarks.length">
            <p v-if="showFullConclusion" class="text-formatted" style="margin-top: -20px;">
              <FormattedNoteText :value="selectedTaskObject.remarks"/>
            </p>
            <p v-if="!showFullConclusion" class="text-formatted" style="margin-top: -20px;">
              <FormattedNoteText :value="selectedTaskObject.remarks" :truncate="300"/>
            </p>
            <div v-if="selectedTaskObject.remarks.length > 300" class="text-center mt-3" @click="showFullConclusion=!showFullConclusion">
              <span class="cursor-pointer" v-if="!showFullConclusion">
                <span class="mr-2">
                  {{ $t('system.view_more') }}
                </span>
                <font-awesome-icon icon="chevron-down"/>
              </span>
              <span class="cursor-pointer" v-if="showFullConclusion">
                <span class="mr-2">
                  {{ $t('system.view_less') }}
                </span>
                <font-awesome-icon icon="chevron-up"/>
              </span>
            </div>
          </div>
        </b-form-group>

        <div v-if="selectedTaskObject.media && selectedTaskObject.media.length">
          <label class="mt-3" style="font-size: medium;">
            {{ $t('maintenance.policies.create.attachments') }}
          </label>
          <br>
          <p v-for="file in selectedTaskObject.media" :key="file.id">
            <span class="mr-3 float-right text-muted cursor-pointer"
                  @click="downloadMedia(file)"
                  data-qid="view-task-download-media"
            >
              <font-awesome-icon icon="cloud-download-alt" />
            </span>
            <a href="#" @click="downloadMedia(file)" data-qid="view-task-media-item">
              {{ file.file_name }}
            </a>
          </p>
        </div>

        <div v-if="selectedTaskObject.links && selectedTaskObject.links.length">
          <label class="mt-3" style="font-size: medium;">
            {{ $t('maintenance.policies.create.links_label') }}
          </label>
          <div v-if="selectedTaskObject.links">
            <p v-for="link in selectedTaskObject.links" :key="link.id">
              <a :href="link.url" target="_blank" data-qid="view-task-link-item">
                {{ link.name ? link.name : link.url }}
              </a>
            </p>
          </div>
          <br>
        </div>
      </div>
    </b-form>
    </section>

    <template #actions>
      <b-button @click="hideViewTaskModal" variant="secondary" qid="view-task-close-button">{{ $t('system.close') }}</b-button>
    </template>
  </TaskPanelFrame>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';
  import TaskPanelFrame from '@/components/tasks/TaskPanelFrame.vue';
  import FormattedNoteText from '@/components/tasks/FormattedNoteText.vue';

  export default {
    name: 'TaskViewModal',
    data: function() {
      return {
        error: '',
        project: {},
        selectedTaskObject: {},
        tasks: [],
        loading: true,

        ownerTask: false,
        ownerDataCollection: false,

        showFullDescription: false,
        showFullControlDescription: false,
        showFullPerformedTest: false,
        showFullConclusion: false,
        showFullProjectMessage: false,
        showFullGenericControlDescription: false,
      }
    },
    props: {
      selectedTask: {},
      showModal: {
        type: Boolean
      }
    },
    components: {
      TaskPanelFrame,
      FormattedNoteText,
      Avatar
    },
    computed: {
      // The task's description, or for documentation collectors the documentation request
      viewDescription() {
        const task = this.selectedTaskObject || {};
        if (this.ownerTask && task.description) {
          return task.description;
        }
        if (!this.ownerTask && this.ownerDataCollection && task.data_collection_description) {
          return task.data_collection_description;
        }
        return '';
      },
      getClient() {
        return this.$store.state.client
      },
      getUser() {
        return this.$store.state.user
      }
    },
    watch: {
      // Watch when prop changes
      showModal: function (value) {
        if (value) {
          this.loadTaskById(this.selectedTask.id)
          //this.showViewTaskModal()
        }
      }
    },
    methods: {
      showViewTaskModal() {
        this.error = '';

        this.$refs['view-task-modal'].show();
      },
      hideViewTaskModal() {
        this.error = '';

        this.$emit('closeViewModal');
        this.$refs['view-task-modal'].hide()
      },
      downloadMedia(object){
        piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.download = object.file_name;

          link.click();
          link.remove()
          window.URL.revokeObjectURL(url);
        }, function() {});
      },
      loadTaskById(id) {
        let self = this;

        piincHttp.get('tasks/' + id).then(function(response) {
          self.selectedTaskObject = response.data;

          if (self.selectedTaskObject.users && self.selectedTaskObject.users.length) {
            _.forEach(self.selectedTaskObject.users, function (user) {
              if (user.id === self.getUser.id) {
                self.ownerTask = true;
              }
            })
          }

          if (self.selectedTaskObject.data_users && self.selectedTaskObject.data_users.length) {
            _.forEach(self.selectedTaskObject.data_users, function (user) {
              if (user.id === self.getUser.id) {
                self.ownerDataCollection = true;
              }
            })
          }

          self.showViewTaskModal()
        }, function() {});
      },

    },
    created () {

    }
  }
</script>
